import { formatAddress, formatName, formatPhone, formatSectionInformation } from './formatting'
import pirTerminology from './pirTerminology'

function formatSignatureSource(sigSource) {
  if (!sigSource) return 'Digital signature'
  return sigSource.toLowerCase() === 'upload' ? 'Signature uploaded' : 'Digital signature'
}

function formatEmailRecipients({ requestorInformation, emailRecipients }) {
  const emails = Object.values(emailRecipients)
  return { 'Recipient Email': emails.filter(e => e !== requestorInformation.email) }
}

function formatEmailRecipientsAsAdditionalRecipients({ requestorInformation, emailRecipients }) {
  const emails = Object.values(emailRecipients)
  return emails
    .filter(e => e !== requestorInformation.email)
    .map(e => {
      return { email: e }
    })
}

function formatPIRDetails({ propertyInformation, requestorInformation }) {
  const formattedDetails = {
    propertyInformation: { 'Property Address': formatAddress(propertyInformation) },
    requestorInformation: {
      'Agency / Brokerage Name': requestorInformation.brokerageName,
      ...formatSectionInformation(requestorInformation)
    }
  }

  if (!requestorInformation.isRealtor) delete formattedDetails.requestorInformation['Agency / Brokerage Name']

  return formattedDetails
}

function formatRequestInformation(requestInformation) {
  if (!requestInformation) return undefined

  const { tier, tierOptions, additionalDetails } = requestInformation
  const hasTier = Boolean(tier)
  const hasAdditionalDetails = Boolean(additionalDetails && additionalDetails.trim())

  if (!hasTier && !hasAdditionalDetails) return undefined

  const formattedRequestInformation = {}

  if (hasTier) {
    formattedRequestInformation['Selected Tier'] = tier
    if (tierOptions && tierOptions.length > 0) formattedRequestInformation.tierOptions = tierOptions
  }

  if (hasAdditionalDetails) formattedRequestInformation['Additional Details'] = additionalDetails

  return formattedRequestInformation
}

function formatStrataSection(strataInformation) {
  return {
    [pirTerminology.getAssociationLabel('Details')]: { [pirTerminology.getAssociationLabel('Number')]: strataInformation.strataNumber },
    'Contact Information': {
      Role: pirTerminology.formatBoardMemberRole(strataInformation.role),
      ...formatSectionInformation(strataInformation)
    }
  }
}

function formatPIRDetailsForDisplay({
  propertyInformation,
  ownerInformation,
  strataInformation,
  requestorInformation,
  emailRecipients,
  requestInformation
} = {}) {
  const formattedDetails = formatPIRDetails({ propertyInformation, ownerInformation, strataInformation, requestorInformation })

  if (strataInformation && strataInformation.strataNumber !== '') {
    formattedDetails.strataInformation = formatStrataSection(strataInformation)
  }

  if (ownerInformation.companyName) {
    formattedDetails.ownerInformation = {
      'Company Details': { 'Company Name': ownerInformation.companyName },
      'Authorized Signer Information': formatSectionInformation(ownerInformation)
    }
  } else {
    formattedDetails.ownerInformation = formatSectionInformation(ownerInformation)
  }

  if (Object.values(emailRecipients).length > 1)
    formattedDetails.requestorInformation['Additional Recipients'] = formatEmailRecipients({ requestorInformation, emailRecipients })

  const formattedRequestInformation = formatRequestInformation(requestInformation)
  if (formattedRequestInformation) formattedDetails.requestInformation = formattedRequestInformation

  return formattedDetails
}

function formatOwnerInformationForReview({ ownerInformation }) {
  return ownerInformation.signatureSource === 'upload'
    ? {
        Name: formatName(ownerInformation),
        Address: ownerInformation.address
      }
    : formatSectionInformation(ownerInformation)
}

function formatPIRForReview(
  { propertyInformation, ownerInformation, strataInformation, requestorInformation, additionalRecipients, requestInformation } = {},
  strataState = 'autoNoStrata'
) {
  const formattedDetails = formatPIRDetails({ propertyInformation, ownerInformation, strataInformation, requestorInformation })

  if (ownerInformation.ownedBy === 'company') {
    formattedDetails.ownerInformation = {
      'Company Details': { 'Company Name': ownerInformation.companyName },
      'Authorized Signer Information': formatOwnerInformationForReview({ ownerInformation })
    }
  } else {
    formattedDetails.ownerInformation = formatOwnerInformationForReview({ ownerInformation })
  }
  formattedDetails.ownerInformation['Owner Authorization'] = { Authorization: formatSignatureSource(ownerInformation.signatureSource) }

  if (ownerInformation.signatureSource === 'upload') formattedDetails.ownerSignature = ownerInformation.signatureFile

  if (['autoStrata', 'manualStrata'].includes(strataState)) {
    formattedDetails.strataInformation = formatStrataSection(strataInformation)
  }

  if (Object.values(additionalRecipients).length > 0)
    formattedDetails.requestorInformation['Additional Recipients'] = { 'Recipient Email': Object.values(additionalRecipients).map(e => e.email) }

  const formattedRequestInformation = formatRequestInformation(requestInformation)
  if (formattedRequestInformation) formattedDetails.requestInformation = formattedRequestInformation

  return formattedDetails
}

function formatPIRForForm(
  { propertyInformation, ownerInformation, requestorInformation, strataInformation, emailRecipients, requestInformation },
  signatureFile = undefined
) {
  const formatted = {
    propertyInformation,
    ownerInformation: {
      ownedBy: ownerInformation.companyName ? 'company' : 'person',
      companyName: ownerInformation.companyName || '',
      signatureSource: ownerInformation.signatureSource.toLowerCase(),
      signatureFile,
      firstName: ownerInformation.firstName,
      lastName: ownerInformation.lastName,
      address: ownerInformation.address,
      phone: formatPhone(ownerInformation.phone) || '',
      confirmEmail: ownerInformation.email || '',
      email: ownerInformation.email || ''
    },
    strataInformation: {
      additionalName: strataInformation?.additionalName || '',
      confirmEmail: strataInformation?.email || '',
      email: strataInformation?.email || '',
      phone: formatPhone(strataInformation?.phone) || '',
      address: strataInformation?.address || '',
      lastName: strataInformation?.lastName || '',
      firstName: strataInformation?.firstName || '',
      role: pirTerminology.formatBoardMemberRole(strataInformation?.role || ''),
      strataNumber: strataInformation?.strataNumber || ''
    },
    requestorInformation: {
      ...requestorInformation,
      phone: formatPhone(requestorInformation.phone),
      confirmEmail: requestorInformation.email
    },
    additionalRecipients: formatEmailRecipientsAsAdditionalRecipients({ requestorInformation, emailRecipients })
  }

  if (requestInformation) {
    formatted.requestInformation = {
      tier: requestInformation.tier || '',
      tierOptions: requestInformation.tierOptions || [],
      additionalDetails: requestInformation.additionalDetails || ''
    }
  }

  const formattedAddress = propertyInformation.formattedAddress
  delete formatted.propertyInformation.formattedAddress

  return { pir: formatted, formattedAddress }
}

function formatPIRForOrderSummary({ propertyInformation, ownerInformation, strataInformation, requestorInformation } = {}) {
  const ownerName = ownerInformation.companyName ? ownerInformation.companyName : formatName(ownerInformation)
  const authorizedSigner = ownerInformation.companyName && formatName(ownerInformation)

  const details = { 'Property Address': formatAddress(propertyInformation) }
  details['Owner Name'] = ownerName
  if (authorizedSigner) details['Authorized Signer Name'] = authorizedSigner
  if (strataInformation) details[pirTerminology.getAssociationLabel('Contact')] = formatName(strataInformation)
  details['Permission Given To'] = formatName(requestorInformation)

  return details
}

function formatPIRForDashboard({ id, propertyInformation, requestorInformation, status, secondaryId, updatedAt } = {}) {
  return {
    id,
    pirId: secondaryId,
    address: propertyInformation.unit ? `${propertyInformation.unit}-${propertyInformation.address}` : propertyInformation.address,
    postalCode: propertyInformation.postalCode,
    requestor: formatName(requestorInformation),
    lastUpdated: new Date(updatedAt),
    status
  }
}

function formatPIRSignatures(signatures) {
  return signatures.map(s => {
    return {
      pirId: s.pirId,
      authorizerRole: s.authorizerRole,
      signatureSource: s.signatureSource,
      status: s.status,
      filename: s.filename
    }
  })
}

function formatAdditionalRecipientsForDownloadLink(requestorKey, emailRecipients) {
  const recipients = JSON.parse(JSON.stringify(emailRecipients))
  delete recipients[requestorKey]
  return Object.keys(recipients).map(r => {
    return { value: r, label: recipients[r] }
  })
}

function formatRecipientsForResendDownloadLink({ requestorInformation, emailRecipients }) {
  const key = Object.keys(emailRecipients).find(e => emailRecipients[e] === requestorInformation.email)
  const emails = { requestor: { label: requestorInformation.email, value: key } }
  if (Object.keys(emailRecipients).length > 1) emails.additionalRecipients = formatAdditionalRecipientsForDownloadLink(key, emailRecipients)

  return emails
}

export {
  formatPIRDetailsForDisplay,
  formatPIRForReview,
  formatPIRForForm,
  formatPIRForOrderSummary,
  formatPIRForDashboard,
  formatPIRSignatures,
  formatEmailRecipientsAsAdditionalRecipients,
  formatAdditionalRecipientsForDownloadLink,
  formatRecipientsForResendDownloadLink,
  formatSignatureSource
}
